import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { DataFlowStream } from 'app/model/dataflow/transformation';
import { Owned } from '../base/named';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { Flowlet } from './flowlet';
import { IExtraFieldInfo } from 'app/interfaces/IExtraFieldInfo';
import { BaseColumnMappingTransformation } from './base-column-mapping-transformation';
import { ParameterValue } from '../base/parameter-value';
import { IMapColumn } from './IMapColumn';
import { format, IViolation } from 'shell-models-lib';
import { DataFlowMapColumn } from './dataflow-map.column';
import { sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { IResourceContext } from 'app/multi-contexts/resource-context.typings';

@Metamodel.model({
    name: 'compose',
    display: [stringResources.DataFlowExecuteFlowletTransformationLabel, stringResources.DataFlowExecuteFlowletTransformationsLabel],
    referable: true,
    constraints: {
        name: {
            required: true
        }
    }
})
export class ExecuteFlowletTransformation extends BaseColumnMappingTransformation {
    public static readonly MODEL: IModel;

    public inspectSummaryOutputFields: IExtraFieldInfo[] = [
        {
            name: () => stringResources.TransformationInspect_NumberOfSplit,
            value: () => this.outputStreamCount
        },
        {
            name: () => stringResources.TransformationInspect_OutputStreams,
            value: () => this.outputStreamNames
        }
    ];

    @Metamodel.attribute({
        display: stringResources.Flowlet,
        constraint: {
            required: true,
            validator: ExecuteFlowletTransformation.validateFlowlet
        },
        reference: true,
        type: Flowlet.MODEL
    })
    public flowlet: Flowlet;

    @Metamodel.attribute({
        transient: true
    })
    public get visibleOutputStreams(): DataFlowStream[] {
        const orderedOutputStreams = Array.from(this.outputStreams);
        return orderedOutputStreams;
    }

    @Metamodel.attribute({
    })
    public parameters: ParameterValue[] = [];

    public inputStreamsWithDuplicates: DataFlowStream[] = [];
    public isSource: boolean = false;
    public isSink: boolean = false;

    @Metamodel.attribute({
        constraint: {
            validator: ExecuteFlowletTransformation.validateMappingTypes
        }
    })
    public schemaMap: Map<number, IMapColumn[]> = new Map<number, IMapColumn[]>();

    public get schema(): IMapColumn[] {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return ([...this.schemaMap.values()] as any).reduce((acc: any, val: any) => acc.concat(val), []);
    }

    @Metamodel.attribute({
        transient: true
    })
    private _currentColumns: Map<DataFlowStream, DataFlowColumn[]> = new Map<DataFlowStream, DataFlowColumn[]>();

    public get columns(): DataFlowColumn[] {
        const columns: DataFlowColumn[] = [];
        this.outputStreams.forEach((stream: DataFlowStream) => columns.push(...this._currentColumns.get(stream)));
        return columns;
    }

    protected get descriptionMsg(): string {
        return stringResources.SplitDescription;
    }

    protected get noDescriptionMsg(): string {
        return stringResources.SplitNoConditionDescription;
    }

    public static validateMappingTypes(transformation: ExecuteFlowletTransformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (transformation.schemaMap.size) {
            transformation.schemaMap.forEach((schema) => {
                schema.forEach((schemaColumn) => {
                    if (schemaColumn instanceof DataFlowMapColumn && schemaColumn.outputType) {
                        if (!schemaColumn.columnRef || !schemaColumn.columnRef.column) {
                            violations.push({
                                context: this,
                                message: () => stringResources.FoldDownDescriptionNoColumns
                            });
                        } else if (!schemaColumn.outputType.compatibleTypes.includes(schemaColumn.columnRef.column.dataFlowColumnType.typeString)) {
                            violations.push({
                                context: this,
                                message: () => sanitizeAndFormatString(stringResources.IncompatibleColumnTypeError, schemaColumn.name, schemaColumn.outputAlias)
                            });
                        }
                    }
                });
            });
        }
        return violations;
    }

    public static validateFlowlet(transformation: ExecuteFlowletTransformation): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (!!transformation.flowlet?.inputs?.size === transformation.isSource) {
            violations.push({
                context: this,
                message: () => stringResources.FlowletLoadFailure
            });
        }
        return violations;
    }

    public getGeneratedDescription(): string {
        if (this.flowlet) {
            format(stringResources.ExecuteFlowletSelectedTransformationDescription, this.flowlet.name);
        }

        return stringResources.ExecuteFlowletTransformationDescription;
    }

    public initializeTransformation(contextData: IResourceContext): void {
        // Don't do anything specific here
    }

    public updateOutputColumns(): void {
        this._currentColumns.clear();

        this.outputStreams.forEach((outputStream) => {
            this._currentColumns.set(outputStream, []);
            const flowletOutput = this.flowlet?.outputs?.find((output) => output.name === outputStream.name);
            if (flowletOutput) {
                flowletOutput.outputColumns().forEach((column) => {
                    const dataflowColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), this);
                    dataflowColumn.name = column.name;
                    dataflowColumn.dataFlowColumnType = DataFlowTypesUtils.cloneType(column.dataFlowColumnType);
                    dataflowColumn.basedOnArguments.push(column);
                    this._currentColumns.get(outputStream).push(dataflowColumn);
                });
            }
        });
    }

    public getAvailableInputStreams(): DataFlowStream[] {
        const streamsToIgnore: DataFlowStream[] = [];
        if (this.outputStreams.size > 0) {
            this.outputStreams.forEach((outputStream) => {
                streamsToIgnore.push(outputStream);
                streamsToIgnore.push(...outputStream.getAllDownStreams());
            });
        }
        return this.owner ? this.owner.getAllOutputStreams()
            .filter((stream) => streamsToIgnore.findIndex((streamToIgnore) => streamToIgnore === stream) === -1) : [];
    }

    public insertInputStream(streamModel: DataFlowStream, index: number = 1): void {
        // always push into duplicates in the incoming index order.
        this.inputStreamsWithDuplicates.push(streamModel);
        if (!this.inputStreams.find((inputStream: DataFlowStream) => inputStream && inputStream.id === streamModel.id)) {
            super.insertInputStream(streamModel, index);
        }
    }

    public detachInputStream(streamModel: DataFlowStream): number {
        this.inputStreamsWithDuplicates = this.inputStreamsWithDuplicates.filter((inputStream: DataFlowStream) => inputStream.id !== streamModel.id);
        return super.detachInputStream(streamModel);
    }

    public removeAllInputStreams(): void {
        this.inputStreamsWithDuplicates.length = 0;
        super.removeAllInputStreams();
    }

    public updateInputStream(oldStream: DataFlowStream, newStream: DataFlowStream): void {
        this.inputStreamsWithDuplicates = this.inputStreamsWithDuplicates
            .map((inputStream) => (inputStream.id === oldStream.id) ? newStream : inputStream);

        super.detachInputStream(oldStream);

        if (!this.inputStreams.find((inputStream: DataFlowStream) => inputStream && inputStream.id === newStream.id)) {
            super.insertInputStream(newStream, this.inputStreams.length);
        }
    }

    public setStream(index: number, value: DataFlowStream): void {
        const previousStream = this.inputStreamsWithDuplicates[index];
        this.inputStreamsWithDuplicates[index] = value;
        if (previousStream) {
            if (!this.inputStreamsWithDuplicates.find((inputStream: DataFlowStream) => inputStream && inputStream.id === previousStream.id)) {
                this.detachInputStream(previousStream);
            }
        }

        if (!this.inputStreams.find((inputStream: DataFlowStream) => inputStream.id === value.id)) {
            super.insertInputStream(value);
        }
    }

    protected updateInputTransformationCacheInternal(): void {
        this.updateOutputColumns();
        super.updateInputTransformationCacheInternal();
    }

    protected _outputColumns(outputStream?: DataFlowStream): DataFlowColumn[] {
        outputStream = outputStream || this.getFirstVisibleOutputStream();

        if (!outputStream) {
            return [];
        }

        if (this._currentColumns.has(outputStream)) {
            return this._currentColumns.get(outputStream);
        }

        this.updateOutputColumns();
        return this._currentColumns.get(outputStream);
    }

    protected getOutputColumnStream(column: DataFlowColumn): DataFlowStream {
        return this.outputStreams.find((stream) => this._outputColumns(stream).findIndex((col) => col === column) >= 0);
    }

    private get outputStreamCount(): number {
        return this.outputStreams.size;
    }

    private get outputStreamNames(): string {
        const names: string[] = [];
        this.outputStreams.forEach((stream) => names.push(stream.name));
        return names.join(', ');
    }
}
